import { push } from 'connected-react-router'
import { config } from '../config'
import { ID_TOKEN } from '../utils'
import { omit } from 'lodash'

const qs = require('query-string')

export const CALL_API = 'CALL_API'
export const METHOD_TYPE = { GET: 'GET', PUT: 'PUT', POST: 'POST', DELETE: 'DELETE' }

/**
 * Call API
 * @param endpoint
 * @param authenticatedRequest
 * @param payload
 * @param method
 * @param token
 * @return {Promise.<TResult>}
 */
const callApi = async (endpoint, authenticatedRequest, payload, method, token) => {
  let queryString = ''
  const options = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    method
  }

  if (authenticatedRequest) {
    options.headers.Authorization = `Bearer ${token}`
  }

  if (payload) {
    if (METHOD_TYPE.GET === method) {
      queryString = `?${qs.stringify(payload)}`
    }

    if ([METHOD_TYPE.POST, METHOD_TYPE.PUT].indexOf(method) > -1) {
      options.body = JSON.stringify(payload)
    }
  }

  const url = config.apiUrl + endpoint + queryString
  const response = await fetch(url, options)
  const json = await response.json()

  if (!response.ok) {
    return Promise.reject(Object.assign({}, { status: response.status }, json))
  }

  if (json && json.success && json.data) {
    const meta = omit(json, 'data')
    const response = {
      data: json.data,
      req: payload,
      meta
    }

    return Promise.resolve(response)
  }

  if (json && 'success' in json && !json.success) {
    return Promise.reject(response)
  }

  return Promise.resolve({
    data: json,
    req: payload
  })
}

export default () => (next) => (action) => {
  const callAPI = action[CALL_API]
  const timestamp = new Date().getTime()

  if (typeof callAPI === 'undefined') {
    return next(action)
  }

  let { endpoint, types, payload, authenticatedRequest, method, req } = callAPI

  if (typeof endpoint !== 'string') {
    throw new Error('Specify a string endpoint URL.')
  }

  if (typeof authenticatedRequest === 'undefined') {
    authenticatedRequest = true
  }

  if (!Array.isArray(types) || types.length !== 3) {
    throw new Error('Expected an array of three action types.')
  }

  if (!types.every((type) => typeof type === 'string')) {
    throw new Error('Expected action types to be strings.')
  }

  if (!method || !METHOD_TYPE[method]) {
    throw new Error('Invalid or missing method type.')
  }

  const [requestType, successType, failureType] = types

  function actionWith (data) {
    const finalAction = Object.assign({}, action, data)
    delete finalAction[CALL_API]
    return finalAction
  }

  function doError (data) {
    if (data.status === 401) {
      return Promise.all([next(data), next(push('/logout'))])
    } else {
      return next(data)
    }
  }

  const token = localStorage.getItem(ID_TOKEN)

  next(actionWith({
    type: requestType,
    payload,
    timestamp,
    req
  }))

  return callApi(endpoint, authenticatedRequest, payload, method, token).then(
    (payload) => {
      return (next)(actionWith({
        payload: (payload && payload.data) || {},
        meta: (payload && payload.meta) || {},
        req: (payload && payload.req) || {},
        authenticatedRequest,
        type: successType,
        timestamp
      }))
    }
  ).catch((error) => (doError)(actionWith({
    type: failureType,
    error: error.message || 'Error!',
    status: error.status,
    timestamp
  })))
}
